import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import escochexLogo from '../../../assets/escochex-logo.webp';
import teamPhoto from '../../../assets/employee-onboarding-team.webp';
import { Button } from '../../../shared/components/Button';
import { PasswordField } from '../../../shared/components/PasswordField';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { useIdentity } from '../../identity/state/useIdentity';
import { ApiError } from '../../identity/types';
import { employeeOnboardingGateway } from '../index';
import { passwordRuleResults } from '../passwordRules';
import { sectionPath, type InvitationCheckResponse } from '../types';
import '../employee-onboarding.css';

type Phase = 'checking' | 'invalid' | 'form' | 'accountExists';

/** Only ever follow a redirect that stays inside the employee onboarding area. */
function safeRedirect(target: string): string {
  return /^\/employee\/[a-z0-9/-]*$/i.test(target) ? target : sectionPath('personal');
}

/**
 * `/employee/accept-invitation?token=...` (Figma 02). Anonymous. The token is checked before any form shows; every bad token
 * (expired, used, revoked, unknown) gets the same message on purpose. The email is the invitation's and cannot be changed.
 */
export function EmployeeAccountCreationPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { refresh } = useIdentity();

  const [phase, setPhase] = useState<Phase>(token ? 'checking' : 'invalid');
  const [invitation, setInvitation] = useState<InvitationCheckResponse | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const emailId = useId();
  const rulesId = useId();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    employeeOnboardingGateway
      .checkInvitation(token)
      .then((r) => {
        if (cancelled) return;
        setInvitation(r);
        setPhase('form');
      })
      .catch(() => {
        if (!cancelled) setPhase('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const rules = passwordRuleResults(password);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!rules.every((r) => r.met)) {
      setFieldError('Your password does not meet all of the requirements yet.');
      return;
    }
    if (password !== confirm) {
      setFieldError('The password and its confirmation do not match.');
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    try {
      const result = await employeeOnboardingGateway.acceptInvitation({ token, password, confirmPassword: confirm });
      // The server has set the session cookies; load the signed-in user before entering the guarded onboarding routes.
      await refresh();
      navigate(safeRedirect(result.redirectTo), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invitation_invalid') {
        setPhase('invalid');
      } else if (err instanceof ApiError && err.code === 'account_exists') {
        setPhase('accountExists');
      } else if (err instanceof ApiError && err.code === 'password_mismatch') {
        setFieldError('The password and its confirmation do not match.');
      } else if (err instanceof ApiError && err.code === 'password_policy') {
        setFieldError('Your password does not meet all of the requirements.');
      } else {
        setSubmitError(friendlyErrorMessage(err, 'We could not create your account. Please try again.'));
      }
      setSubmitting(false);
    }
  }

  let content;
  if (phase === 'checking') {
    content = (
      <p className="eo-loading" role="status">
        Checking your invitation…
      </p>
    );
  } else if (phase === 'invalid') {
    content = (
      <div data-testid="invitation-invalid">
        <h1 className="eo-account__title">Profile Setup Link Expired!</h1>
        <p className="eo-banner eo-banner--error" role="alert">
          This invitation link has expired or has already been used. Please request a new invitation from your administrator.
        </p>
      </div>
    );
  } else if (phase === 'accountExists') {
    content = (
      <div data-testid="account-exists">
        <h1 className="eo-account__title">You already have an account</h1>
        <p className="eo-banner eo-banner--info" role="status">
          An Escochex account already exists for this email. Sign in with it instead of creating a new one.
        </p>
        <p>
          <Link to="/login" className="eo-link-button">
            Go to Sign In
          </Link>
        </p>
      </div>
    );
  } else {
    content = (
      <form onSubmit={handleSubmit} noValidate aria-labelledby="eo-account-title">
        <h1 id="eo-account-title" className="eo-account__title">
          Employee Account Creation
        </h1>
        {invitation && (
          <p className="eo-note">
            Welcome, {invitation.employeeFirstName}. Create your password to join {invitation.companyName}.
          </p>
        )}
        <div className="eo-field">
          <label htmlFor={emailId} className="eo-field__label">
            Email Address <span className="eo-field__req" aria-hidden="true">*</span>
          </label>
          <input id={emailId} className="eo-input" type="email" value={invitation?.email ?? ''} readOnly aria-readonly="true" autoComplete="username" />
        </div>

        <PasswordField
          label="Password"
          autoComplete="new-password"
          aria-required="true"
          aria-describedby={rulesId}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <ul id={rulesId} className="eo-rules" aria-label="Password requirements">
          {rules.map((r) => (
            <li key={r.id} className={r.met ? 'eo-rules__item eo-rules__item--met' : 'eo-rules__item'} data-met={r.met}>
              <span className="eo-rules__box" aria-hidden="true">
                {r.met ? '✓' : ''}
              </span>
              {r.label}
              <span className="eo-sr-only">{r.met ? ' (met)' : ' (not met yet)'}</span>
            </li>
          ))}
        </ul>

        <PasswordField
          label="Confirm Password"
          autoComplete="new-password"
          aria-required="true"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        {fieldError && (
          <p className="eo-banner eo-banner--error" role="alert">
            {fieldError}
          </p>
        )}
        {submitError && (
          <p className="eo-banner eo-banner--error" role="alert">
            {submitError}
          </p>
        )}
        <div className="eo-account__submit">
          <Button type="submit" className="eo-btn eo-btn--primary" loading={submitting} loadingLabel="Saving…">
            Save and Continue
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="eo-page">
      <div className="eo-account">
        <div className="eo-account__photo">
          <img src={teamPhoto} alt="" />
        </div>
        <div className="eo-account__form">
          <img className="eo-account__logo" src={escochexLogo} alt="Escochex — A Payroll Software Company" />
          {content}
        </div>
      </div>
    </div>
  );
}
